import type { PTC_FARE_CATEGORY } from '@/constants/components/fareType'
import { getChangeInfo, getTransitVisaRequired } from '@/utils/srp/flightDetails'
import { getPtcFare } from '@/utils/srp/helper'

import { parseAirportTime } from './airportTime'
import {
  MAX_TUPLE_TAGS,
  TAG_PRIORITY_ORDER_DOM,
  TAG_PRIORITY_ORDER_INTL,
  type JourneyLeg,
} from '../config'
import type {
  TupleAirline,
  TupleEndpoint,
  TupleFilterKeys,
  TupleLayover,
  TupleMetaTag,
  TupleOneWayViewModel,
  TuplePrice,
  TupleRoundTripViewModel,
  TupleStop,
} from '../model/types'

// ── Wire shapes (typed only what we read) ────────────────────────────────────

interface WireAirport {
  code: string
  time: string
  terminal?: { name?: string; gate?: string }
}

interface WireDuration {
  hh: number
  mm: number
}

interface WireSummary {
  flights: Array<{ airlineCode: string; flightNumber: string }>
  firstDeparture: { airlineCode: string; airport: WireAirport }
  lastArrival: { airlineCode: string; airport: WireAirport }
  stops: number
  totalDuration: WireDuration
  timelineText: string
  lowestRoundTripPriceDetails?: {
    perAdultPricing?: { displayPrice: number | null; strikeOffPrice?: number | null }
    totalPricing?: { displayPrice: number | null; strikeOffPrice?: number | null }
  }
}

interface WireLayover {
  airport: string
  totalDuration: { hh: number; mm: number }
  transitVisaRequired?: boolean
}

interface WireCard {
  cardId?: string | number
  travelOptionId: string
  subTravelOptionIds: string[]
  summary: WireSummary
  layover?: WireLayover[]
  samePriceCards?: Array<string | number>
  // flightInsights?: { onTimePercentage?: number } // OTP
}

interface WireBenefitTag {
  benefitType?: string
  displayText?: string
  benefitValue?: string
  logo?: string | null
}

interface WireFareListItem {
  fareId?: string
  benefitTags?: WireBenefitTag[]
}

interface WireSubTravelOption {
  cheapestFareId?: string
  fareIds?: string[]
  sortList?: string[]
  sequenceToFlightIdMap?: Record<string, string>
  tags?: WireBenefitTag[]
  fareList?: WireFareListItem[]
}

interface WireCouponDetails {
  message?: string | null
  adultCouponMessage?: string | null
  couponCode?: string | null
  discountAmount?: number | null
  discountedPrice?: number | null
}

interface WirePricingBlock {
  totalPrice?: number
  totalTax?: number
  totalBaseFare?: number
  strikeOffDetails?: { displayPrice?: number | null; strikeOffPrice?: number | null }
  couponDetails?: WireCouponDetails
}

interface WireFare {
  fareId: string
  fareCategory?: string
  checkInBaggageAllowed?: boolean
  benefitTags?: WireBenefitTag[]
  pricing: {
    totalPricing: WirePricingBlock
    perAdultPricing: WirePricingBlock
  }
}

interface WireMetaData {
  airlineDetail?: Record<string, { name?: string; logoURL?: string }>
  airportDetail?: Record<string, { city?: string; name?: string; countryCode?: string }>
}

export interface NormaliseContext {
  subTravelOptions: Record<string, WireSubTravelOption>
  fares: Record<string, WireFare>
  metaData: WireMetaData
  flights?: Record<string, any>
  showPerAdult?: boolean
  ptcFareType?: string
  /** Selects DOM vs INTL tag priority lists. */
  isIntl?: boolean
  /** Domestic nearby codes from filterData; omit for intl (use searchedOrigin/Destination). */
  nearbyAirportCodes?: Set<string>
  /** Intl nearby: dep ≠ this → nearby. For J2 pass swapped origin/destination. */
  searchedOrigin?: string
  searchedDestination?: string
}

interface WireNearbyAirportFilter {
  airportCode?: string
  selectedValues?: string[]
}

interface WireFilterLeg {
  nearbyAirportFilter?: WireNearbyAirportFilter[]
}

/** Fields `toNormaliseContext` / RT seed actually read from the search response. */
export interface WireSearchResponse {
  dataId?: string
  metaData?: WireMetaData
  subTravelOptions?: Record<string, WireSubTravelOption>
  fares?: Record<string, WireFare>
  flights?: Record<string, any>
  filterData?: {
    onwardLeg?: WireFilterLeg
    returnLeg?: WireFilterLeg
  }
  cards?: {
    J1?: unknown[]
    J2?: unknown[]
  }
  specialRTFareMap?: Record<string, { fareIds?: string[] }>
  /** High-RPS travelOptionIds from search (not WOW). */
  highRpsTravelOptionIds?: string[]
  /** Server-ordered cardId lists keyed by sortKey → journey. */
  sortData?: Record<string, { J1?: Array<string | number>; J2?: Array<string | number> }>
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const

/** Wall-clock `YYYY-MM-DD` from an ISO timestamp (avoids browser-TZ shifts). */
const wallDateParts = (iso: string): { year: number; month: number; day: number } | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso)
  if (!match) return null
  return { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) }
}

/** Wall-clock HH:mm from wire ISO — never `Date#getHours` (TZ shift vs prod). */
const parseTime = (isoTime: string): string => {
  const match = /T(\d{2}):(\d{2})/.exec(isoTime ?? '')
  if (match) return `${match[1]}:${match[2]}`
  return isoTime?.slice(11, 16) ?? ''
}

/** Calendar-day gap between dep/arr wall clocks — not elapsed ms / 86400000. */
const calendarDayOffset = (depIso: string, arrIso: string): number => {
  const dep = wallDateParts(depIso)
  const arr = wallDateParts(arrIso)
  if (!dep || !arr) return 0
  const depUtc = Date.UTC(dep.year, dep.month - 1, dep.day)
  const arrUtc = Date.UTC(arr.year, arr.month - 1, arr.day)
  return Math.round((arrUtc - depUtc) / 86_400_000)
}

const formatArrivalDateLabel = (arrIso: string): string | undefined => {
  const parts = wallDateParts(arrIso)
  if (!parts) return undefined
  return `${parts.day} ${MONTH_SHORT[parts.month - 1]}`
}

const durationLabel = (duration?: WireDuration): string => {
  if (!duration) return ''
  const parts: string[] = []
  if (duration.hh) parts.push(`${duration.hh}h`)
  if (duration.mm) parts.push(`${duration.mm}m`)
  return parts.join(' ')
}

const cheapestFare = (card: WireCard, ctx: NormaliseContext): WireFare | undefined => {
  for (const stoId of card?.subTravelOptionIds || []) {
    const sto = ctx?.subTravelOptions?.[stoId]
    if (!sto) continue
    let fareIds = sto.fareIds?.length ? sto.fareIds : sto.cheapestFareId ? [sto.cheapestFareId] : []
    if (ctx.ptcFareType) {
      const ptcFares = getPtcFare(fareIds, ctx.fares as any, ctx.ptcFareType as keyof typeof PTC_FARE_CATEGORY)
      if (ptcFares?.length) {
        fareIds = ptcFares
      }
    }
    const fareId = ctx.ptcFareType && fareIds[0] ? fareIds[0] : (sto.cheapestFareId || fareIds[0])
    if (fareId && ctx?.fares?.[fareId]) return ctx.fares[fareId]
  }
  return undefined
}

const toPrice = (fare: WireFare | undefined, showPerAdult: boolean): TuplePrice => {
  if (!fare) return { displayPrice: 0 }
  const pricing = showPerAdult ? fare.pricing?.perAdultPricing : fare.pricing?.totalPricing
  // strikeOffDetails.displayPrice is post-discount; strikeOffPrice is the original
  const strikeDetails = pricing?.strikeOffDetails
  const display = strikeDetails?.displayPrice ?? pricing?.totalPrice ?? 0
  const strike = strikeDetails?.strikeOffPrice ?? undefined
  const couponDiscounted = pricing?.couponDetails?.discountedPrice
  const totalPricing = fare.pricing?.totalPricing
  return {
    displayPrice: display || 0,
    strikeOffPrice: strike != null && strike > (display || 0) ? strike : undefined,
    couponDiscountedPrice: couponDiscounted != null && couponDiscounted > 0 ? couponDiscounted : undefined,
    showPerAdult,
    ...(showPerAdult
      ? {
          totalBaseFare: totalPricing?.totalBaseFare ?? 0,
          totalTax: totalPricing?.totalTax ?? 0,
          totalPrice: totalPricing?.totalPrice ?? 0,
        }
      : {}),
  }
}

const toAirlines = (summary: WireSummary, metaData: WireMetaData): TupleAirline[] => {
  const seen = new Set<string>()
  const result: TupleAirline[] = []
  for (const flight of summary?.flights || []) {
    if (!flight?.airlineCode) continue
    if (seen.has(flight.airlineCode)) continue
    seen.add(flight.airlineCode)
    result.push({
      code: flight.airlineCode,
      name: metaData?.airlineDetail?.[flight.airlineCode]?.name ?? flight.airlineCode,
      flightNumber: `${flight.airlineCode}-${flight.flightNumber}`,
    })
  }
  return result
}

const norm = (v?: string) => (v ?? '').trim().toUpperCase()

const toEndpoint = (
  raw: { airlineCode?: string; airport?: WireAirport } | undefined,
  metaData: WireMetaData,
  nearbyAirportCodes: Set<string> | undefined,
  searchedCode: string | undefined
): TupleEndpoint => {
  const code = raw?.airport?.code ?? ''
  const airportInfo = metaData?.airportDetail?.[code]
  const searchedInfo = searchedCode ? metaData?.airportDetail?.[searchedCode] : undefined
  const city = airportInfo?.city?.trim() || ''
  const airportName = airportInfo?.name?.trim() || ''
  const searchedCity = searchedInfo?.city?.trim() || searchedInfo?.name?.trim() || searchedCode
  // Domestic: code in filter selectedValues. Intl: code ≠ searched IATA code.
  const isNearby = nearbyAirportCodes
    ? nearbyAirportCodes.has(code)
    : searchedCode
      ? norm(code) !== norm(searchedCode)
      : false
  const displayName = city || code
  return {
    time: parseAirportTime(raw?.airport?.time ?? ''),
    city: displayName,
    displayName,
    airportCode: code,
    airportName: airportName || displayName,
    searchedCity,
    isNearby,
    nearbyTooltip: isNearby ? `${displayName} (${code}) is a nearby alternate airport` : undefined,
  }
}

/** Ordered flight segments — same path FlightDetailsBody uses. */
const getSequenceFlights = (card: WireCard, ctx: NormaliseContext): any[] => {
  const stoId = card.subTravelOptionIds?.[0]
  const map = stoId ? ctx.subTravelOptions?.[stoId]?.sequenceToFlightIdMap : undefined
  if (!map || !ctx.flights) return []
  return Object.keys(map)
    .sort((a, b) => Number(a) - Number(b))
    .map(k => ctx.flights![map[k]])
    .filter(Boolean)
}

/** O(flights) index: technical stops + connection arrivals → next flight. */
const buildFlightIndex = (sequenceFlights: any[]) => {
  const techByAirport = new Map<string, { flight: any; stop: any }>()
  const connectionByAirport = new Map<string, { prev: any; next: any; prevIndex: number }>()

  for (let i = 0; i < sequenceFlights.length; i++) {
    const flight = sequenceFlights[i]
    for (const stop of flight?.stops || []) {
      const code = norm(stop?.airportCode)
      if (code && !techByAirport.has(code)) techByAirport.set(code, { flight, stop })
    }
    if (i < sequenceFlights.length - 1) {
      const code = norm(flight?.arrival?.airport?.code)
      if (code && !connectionByAirport.has(code)) {
        connectionByAirport.set(code, { prev: flight, next: sequenceFlights[i + 1], prevIndex: i })
      }
    }
  }
  return { techByAirport, connectionByAirport }
}

const flightIdentity = (flight?: any, summary?: { airlineCode?: string; flightNumber?: string }) => {
  const fltNo = String(flight?.fltNo || flight?.flightNumber || summary?.flightNumber || '').trim()
  const airline = norm(flight?.airlineCode || summary?.airlineCode)
  return fltNo && airline ? `${airline}:${fltNo}` : ''
}

/** Intermediate airports from STO id when wire omits `card.layover`. */
const stopCodesFromStoIds = (subTravelOptionIds?: string[]): string[] => {
  if (!Array.isArray(subTravelOptionIds)) return []
  for (const sId of subTravelOptionIds) {
    if (typeof sId !== 'string' || !sId.includes('__')) continue
    const codes = sId
      .split('__')
      .slice(0, -1)
      .map(seg => {
        const parts = seg.split('-')
        return parts.length >= 4 ? parts[parts.length - 2] : ''
      })
      .filter(Boolean)
    if (codes.length) return Array.from(new Set(codes))
  }
  return []
}

const fallbackStopCodes = (card: WireCard, sequenceFlights: any[]): string[] => {
  const fromSto = stopCodesFromStoIds(card.subTravelOptionIds)
  if (fromSto.length) return fromSto
  const codes: string[] = []
  for (let i = 0; i < sequenceFlights.length; i++) {
    for (const stop of sequenceFlights[i]?.stops || []) {
      if (stop?.airportCode) codes.push(stop.airportCode)
    }
    if (i < sequenceFlights.length - 1) {
      const arr = sequenceFlights[i]?.arrival?.airport?.code
      if (arr) codes.push(arr)
    }
  }
  return codes
}

const toStop = (card: WireCard, ctx: NormaliseContext): TupleStop => {
  const count = card?.summary?.stops ?? 0
  if (count <= 0) return { count: 0, label: 'Non stop', layovers: [] }

  const sequenceFlights = getSequenceFlights(card, ctx)
  const summaryFlights = card.summary?.flights || []
  const airportDetail = ctx.metaData?.airportDetail
  const rawLayovers = card.layover || []
  const flightIndex = buildFlightIndex(sequenceFlights)

  const resolveLayover = (
    airportCode: string,
    hours: number,
    minutes: number,
    fallbackIndex: number,
    layoverTransitVisa?: boolean
  ): TupleLayover => {
    const code = norm(airportCode)
    const tech = code ? flightIndex.techByAirport.get(code) : undefined
    const isTechnical = Boolean(tech)

    let arrival: any
    let departure: any
    let prevFlight: any
    let nextFlight: any
    let prevIndex: number | undefined

    if (tech) {
      const stop = tech.stop
      const stopCode = stop?.airportCode || airportCode
      const terminal = stop?.arrivalTerminal || stop?.terminal
      arrival = { airport: { code: stopCode, time: stop?.arrTime, terminal } }
      departure = {
        airport: {
          code: stopCode,
          time: stop?.depTime,
          terminal: stop?.departureTerminal || stop?.terminal,
        },
      }
      prevFlight = nextFlight = tech.flight
    } else {
      const conn = code ? flightIndex.connectionByAirport.get(code) : undefined
      if (conn) {
        prevFlight = conn.prev
        nextFlight = conn.next
        prevIndex = conn.prevIndex
        arrival = prevFlight?.arrival
        departure = nextFlight?.departure
      } else if (fallbackIndex >= 0) {
        prevFlight = sequenceFlights[fallbackIndex]
        nextFlight = sequenceFlights[fallbackIndex + 1]
        prevIndex = fallbackIndex
        arrival = prevFlight?.arrival
        departure = nextFlight?.departure
      }
    }

    const changeMessages: string[] = []
    if (isTechnical) changeMessages.push('Deboarding may not be allowed')

    const prevId = flightIdentity(prevFlight, prevIndex != null ? summaryFlights[prevIndex] : summaryFlights[fallbackIndex])
    const nextId = flightIdentity(
      nextFlight,
      prevIndex != null ? summaryFlights[prevIndex + 1] : summaryFlights[fallbackIndex + 1]
    )
    const planeChange = !isTechnical && Boolean(prevId && nextId && prevId !== nextId)
    if (planeChange) changeMessages.push('Change of planes')

    const transitVisaRequired =
      Boolean(layoverTransitVisa) || (arrival ? getTransitVisaRequired(arrival, rawLayovers) : false)
    if (transitVisaRequired) changeMessages.push('Transit visa may be required')

    const changeInfo =
      arrival && departure
        ? getChangeInfo(arrival, departure, airportDetail)
        : { airportChange: false, terminalChange: false, message: '' }
    if (changeInfo.terminalChange) changeMessages.push('Change of terminals')
    if (changeInfo.airportChange && changeInfo.message) changeMessages.push(changeInfo.message)

    return {
      airportCode,
      airportCity: airportDetail?.[airportCode]?.city ?? airportCode,
      hours,
      minutes,
      planeChange,
      terminalChange: changeInfo.terminalChange,
      airportChange: changeInfo.airportChange,
      transitVisaRequired,
      changeMessages,
    }
  }

  const layovers: TupleLayover[] =
    rawLayovers.length > 0
      ? rawLayovers.map((layover, i) =>
          resolveLayover(
            layover?.airport ?? '',
            layover?.totalDuration?.hh ?? 0,
            layover?.totalDuration?.mm ?? 0,
            i,
            layover?.transitVisaRequired
          )
        )
      : fallbackStopCodes(card, sequenceFlights).map((airportCode, i) => resolveLayover(airportCode, 0, 0, i))

  const stopCodes = Array.from(new Set(layovers.map(l => l.airportCode).filter(Boolean)))
  const stopInfo = stopCodes.length ? ` at ${stopCodes.join(', ')}` : ''
  return {
    count,
    label: count === 1 ? `1 stop${stopInfo}` : `${count} stops${stopInfo}`,
    layovers,
  }
}

const BENEFIT_TYPE_TO_TAG_ID: Record<string, string> = {
  TRANSIT_VISA: 'transitVisa',
  SELF_TRANSFER: 'selfTransfer',
  AIRPORT_CHANGE: 'selfTransfer',
  TERMINAL_CHANGE: 'terminalChange',
  OPERATED_BY: 'operatedBy',
  HAND_BAGGAGE: 'handBaggage',
  NEARBY_AIRPORT: 'nearbyAirport',
  PARTIAL_REFUNDABLE: 'refundable',
  REFUNDABLE: 'refundable',
  NON_REFUNDABLE: 'nonRefundable',
  CT_UPGRADE: 'ctUpgrade',
  VISA_REJECTION_COVER: 'visaRejectionCover',
}

const PTC_STUDENT_BENEFIT_TYPES = new Set(['STUDENT_FARE', 'STUDENT_FARE_AVAILABLE'])

const STUDENT_FARE_TAG: TupleMetaTag = { id: 'studentFare', label: 'Student fare available' }

/** Same signal domestic + intl filters use: any STO fare with RETAIL_STUDENT. */
const cardHasStudentFare = (card: WireCard, ctx: NormaliseContext): boolean => {
  for (const stoId of card.subTravelOptionIds ?? []) {
    const sto = ctx.subTravelOptions[stoId]
    if (!sto) continue
    const fareIds = sto.fareIds?.length ? sto.fareIds : sto.cheapestFareId ? [sto.cheapestFareId] : []
    for (const fareId of fareIds) {
      if (ctx.fares[fareId]?.fareCategory === 'RETAIL_STUDENT') return true
    }
  }
  return false
}

const fareBenefitTags = (sto: WireSubTravelOption, ctx: NormaliseContext): WireBenefitTag[] => {
  const cheapId = sto.cheapestFareId || sto.fareIds?.[0]
  const cheapFare = cheapId ? ctx.fares[cheapId] : undefined
  const fromFareList =
    sto.fareList?.find(item => item.fareId === cheapId)?.benefitTags ?? sto.fareList?.[0]?.benefitTags
  const benefitTags = cheapFare?.benefitTags ?? fromFareList ?? []
  return [...benefitTags, ...(sto.tags ?? [])]
}

const toTagId = (tag: WireBenefitTag, label: string): string =>
  (tag.benefitType ? BENEFIT_TYPE_TO_TAG_ID[tag.benefitType] : undefined) ?? tag.benefitType ?? label

const toTags = (card: WireCard, ctx: NormaliseContext): TupleMetaTag[] => {
  const tagsById = new Map<string, TupleMetaTag>()

  if (cardHasStudentFare(card, ctx)) {
    tagsById.set(STUDENT_FARE_TAG.id, STUDENT_FARE_TAG)
  }

  for (const stoId of card.subTravelOptionIds) {
    const sto = ctx.subTravelOptions[stoId]
    if (!sto) continue
    const wireTags = fareBenefitTags(sto, ctx)
    if (!wireTags.length) continue

    for (const wireTag of wireTags) {
      if (wireTag.benefitType && PTC_STUDENT_BENEFIT_TYPES.has(wireTag.benefitType)) continue
      const label = wireTag.displayText
      if (!label) continue
      const id = toTagId(wireTag, label)
      if (!tagsById.has(id)) tagsById.set(id, { id, label })
    }
    break
  }

  // OTP — re-enable later: uncomment WireCard.flightInsights, onTimeTagLabel import,
  // onTimePercent below, and this block:
  // const onTimePercentage = card.flightInsights?.onTimePercentage
  // if (onTimePercentage != null && onTimePercentage > 0) {
  //   tagsById.set('onTimePerformance', {
  //     id: 'onTimePerformance',
  //     label: onTimeTagLabel(onTimePercentage),
  //   })
  // }

  const priorityOrder = ctx.isIntl ? TAG_PRIORITY_ORDER_INTL : TAG_PRIORITY_ORDER_DOM
  const tags: TupleMetaTag[] = []
  for (const id of priorityOrder) {
    const tag = tagsById.get(id)
    if (!tag) continue
    tags.push(tag)
    if (tags.length === MAX_TUPLE_TAGS) break
  }
  return tags
}

const cheapestFareId = (card: WireCard, ctx: NormaliseContext): string => {
  for (const stoId of card.subTravelOptionIds) {
    const sto = ctx.subTravelOptions[stoId]
    if (!sto) continue
    const fareIds = sto.fareIds?.length ? sto.fareIds : sto.cheapestFareId ? [sto.cheapestFareId] : []
    if (ctx.ptcFareType) {
      const ptcFares = getPtcFare(fareIds, ctx.fares as any, ctx.ptcFareType as keyof typeof PTC_FARE_CATEGORY)
      if (ptcFares?.length) {
        return ptcFares[0]
      }
    }
    if (sto.cheapestFareId) return sto.cheapestFareId
    if (sto.fareIds?.[0]) return sto.fareIds[0]
  }
  return ''
}

const toCouponCallout = (fare: ReturnType<typeof cheapestFare>, showPerAdult: boolean): string | undefined => {
  const pricing = showPerAdult ? fare?.pricing.perAdultPricing : fare?.pricing.totalPricing
  const couponDetails = pricing?.couponDetails
  if (!couponDetails) return undefined
  const raw = (showPerAdult ? couponDetails.adultCouponMessage : couponDetails.message) ?? ''
  if (!raw) return undefined
  return (
    raw
      .replace('{couponCode}', couponDetails.couponCode ?? '')
      .replace('{discountAmount}', couponDetails.discountAmount != null ? `₹${couponDetails.discountAmount}` : '') ||
    undefined
  )
}

/** Wall-clock HHmm integer from an ISO timestamp (legacy filter unit). */
const toHhmm = (iso: string): number => {
  const match = /T(\d{2}):(\d{2})/.exec(iso ?? '')
  if (match) return parseInt(`${match[1]}${match[2]}`, 10)
  return -1
}

const displayPriceOf = (fare: WireFare | undefined, perAdult: boolean): number => {
  if (!fare) return 0
  const pricing = perAdult ? fare.pricing.perAdultPricing : fare.pricing.totalPricing
  return pricing?.strikeOffDetails?.displayPrice ?? pricing?.totalPrice ?? 0
}

const toFilterKeys = (card: WireCard, ctx: NormaliseContext): TupleFilterKeys => {
  const summary = card.summary
  const airlineCodes = (summary.flights ?? []).map(f => f.airlineCode).filter(Boolean)
  const layovers = card.layover ?? []
  let layoverMinutes = 0
  const layoverAirportCodes: string[] = []
  for (const layover of layovers) {
    layoverMinutes += (layover.totalDuration?.hh ?? 0) * 60 + (layover.totalDuration?.mm ?? 0)
    if (layover.airport) layoverAirportCodes.push(layover.airport.toUpperCase())
  }

  let checkInBaggageAllowed = false
  const hasStudentFare = cardHasStudentFare(card, ctx)
  let hasCtUpgrade = false
  let hasVisaRejection = false

  for (const stoId of card.subTravelOptionIds ?? []) {
    const sto = ctx.subTravelOptions[stoId]
    if (!sto) continue
    const fareIds = sto.fareIds?.length ? sto.fareIds : sto.cheapestFareId ? [sto.cheapestFareId] : []
    for (const fareId of fareIds) {
      const f = ctx.fares[fareId]
      if (!f) continue
      if (f.checkInBaggageAllowed) checkInBaggageAllowed = true
    }
    // Legacy filters cheapest fare's benefitTags; fareList[0] is a fallback.
    const cheapId = sto.cheapestFareId || sto.fareIds?.[0]
    const cheapFare = cheapId ? ctx.fares[cheapId] : undefined
    const fromFareList =
      sto.fareList?.find(item => item.fareId === cheapId)?.benefitTags ?? sto.fareList?.[0]?.benefitTags
    const benefitTags = cheapFare?.benefitTags ?? fromFareList ?? sto.tags ?? []
    for (const tag of benefitTags) {
      if (tag.benefitType === 'CT_UPGRADE') hasCtUpgrade = true
      if (tag.benefitType === 'VISA_REJECTION_COVER') hasVisaRejection = true
    }
  }

  const fare = cheapestFare(card, ctx)
  const lowest = summary.lowestRoundTripPriceDetails

  return {
    travelOptionId: card.travelOptionId,
    airlineCodes,
    isMultiAirline: airlineCodes.length > 1 && !airlineCodes.every(c => c === airlineCodes[0]),
    departureHhmm: toHhmm(summary.firstDeparture?.airport?.time ?? ''),
    arrivalHhmm: toHhmm(summary.lastArrival?.airport?.time ?? ''),
    departureAirportCode: (summary.firstDeparture?.airport?.code ?? '').toUpperCase(),
    arrivalAirportCode: (summary.lastArrival?.airport?.code ?? '').toUpperCase(),
    durationMinutes: (summary.totalDuration?.hh ?? 0) * 60 + (summary.totalDuration?.mm ?? 0),
    layoverMinutes,
    layoverAirportCodes,
    cheapestDisplayPrice: displayPriceOf(fare, false),
    cheapestDisplayPriceAdt: displayPriceOf(fare, true),
    lowestRoundTripDisplayPrice: lowest?.totalPricing?.displayPrice ?? undefined,
    lowestRoundTripDisplayPriceAdt: lowest?.perAdultPricing?.displayPrice ?? undefined,
    checkInBaggageAllowed,
    hasStudentFare,
    hasCtUpgrade,
    hasVisaRejection,
  }
}

// ── Public API ─────────────────────────────────────────────────────────────────

export const toTupleOneWayViewModel = (card: WireCard, ctx: NormaliseContext): TupleOneWayViewModel | null => {
  try {
    if (!card || !ctx) return null
    const { metaData, showPerAdult = false, nearbyAirportCodes, searchedOrigin, searchedDestination } = ctx
    const summary = card?.summary || ({} as WireSummary)
    const fare = cheapestFare(card, ctx)
    const depIso = summary?.firstDeparture?.airport?.time ?? ''
    const arrIso = summary?.lastArrival?.airport?.time ?? ''
    const offset = calendarDayOffset(depIso, arrIso)
    const dep = toEndpoint(summary?.firstDeparture, metaData, nearbyAirportCodes, searchedOrigin)
    const arr: TupleEndpoint = {
      ...toEndpoint(summary?.lastArrival, metaData, nearbyAirportCodes, searchedDestination),
      dayOffset: offset > 0 ? offset : undefined,
      arrivalDateLabel: offset > 0 ? formatArrivalDateLabel(arrIso) : undefined,
    }

    const cardId = card.cardId != null ? String(card.cardId) : card.travelOptionId
    const samePriceCardIds = Array.isArray(card.samePriceCards)
      ? card.samePriceCards.map(id => String(id)).filter(Boolean)
      : []

    const flightNumbers = (summary?.flights || [])
      .map(f => {
        const code = f.airlineCode || ''
        const num = f.flightNumber || ''
        return num ? `${code} ${num}` : code
      })
      .filter(Boolean)
    const flightNumbersLabel = flightNumbers.join(', ')

    return {
      id: card.travelOptionId,
      cardId,
      travelOptionId: card.travelOptionId,
      fareId: cheapestFareId(card, ctx),
      // onTimePercent: card.flightInsights?.onTimePercentage, // OTP
      airlines: toAirlines(summary, metaData),
      flightNumbersLabel: flightNumbersLabel || undefined,
      departure: dep,
      arrival: arr,
      durationLabel: durationLabel(summary.totalDuration),
      stop: toStop(card, ctx),
      price: toPrice(fare, showPerAdult),
      tags: toTags(card, ctx),
      samePriceCardIds,
      couponCallout: toCouponCallout(fare, showPerAdult),
      filter: toFilterKeys(card, ctx),
    }
  } catch (error) {
    console.error('toTupleOneWayViewModel mapping error', {
      cardId: card?.cardId,
      travelOptionId: card?.travelOptionId,
      error,
    })
    return null
  }
}

/**
 * Normalise all J1 (or J2) cards from the wire response into `TupleOneWayViewModel[]`.
 * For RT use `toTupleRoundTripCardsForLeg` instead.
 */
export const toTupleCardsForLeg = (rawCards: unknown[], ctx: NormaliseContext): TupleOneWayViewModel[] => {
  if (!Array.isArray(rawCards)) return []
  return rawCards
    .map(rawCard => toTupleOneWayViewModel(rawCard as WireCard, ctx))
    .filter((vm): vm is TupleOneWayViewModel => vm !== null)
}

export const toTupleRoundTripViewModel = (
  card: WireCard,
  ctx: NormaliseContext,
  leg: JourneyLeg
): TupleRoundTripViewModel | null => {
  try {
    if (!card || !ctx) return null
    const base = toTupleOneWayViewModel(card, ctx)
    if (!base) return null
    const lowestDetails = card?.summary?.lowestRoundTripPriceDetails
    const pricingBlock = ctx?.showPerAdult ? lowestDetails?.perAdultPricing : lowestDetails?.totalPricing
    const lowestRoundTripPrice = pricingBlock?.displayPrice ?? undefined
    const lowestRoundTripStrikePrice = pricingBlock?.strikeOffPrice ?? undefined

    return {
      ...base,
      id: `${leg}-${card.travelOptionId}`,
      travelOptionId: card.travelOptionId,
      leg,
      lowestRoundTripPrice: lowestRoundTripPrice ?? undefined,
      lowestRoundTripStrikePrice:
        lowestRoundTripStrikePrice != null && lowestRoundTripStrikePrice > (lowestRoundTripPrice ?? 0)
          ? lowestRoundTripStrikePrice
          : undefined,
    }
  } catch (error) {
    console.error('toTupleRoundTripViewModel mapping error', {
      cardId: card?.cardId,
      travelOptionId: card?.travelOptionId,
      leg,
      error,
    })
    return null
  }
}

/**
 * Normalise raw wire cards for a given leg into `TupleRoundTripViewModel[]`.
 * Cards are sorted cheapest-first by their own price.
 */
export const toTupleRoundTripCardsForLeg = (
  rawCards: unknown[],
  ctx: NormaliseContext,
  leg: JourneyLeg
): TupleRoundTripViewModel[] => {
  if (!Array.isArray(rawCards)) return []
  const viewModels = rawCards
    .map(rawCard => toTupleRoundTripViewModel(rawCard as WireCard, ctx, leg))
    .filter((vm): vm is TupleRoundTripViewModel => vm !== null)
  return viewModels.sort((a, b) => (a.price.displayPrice ?? 0) - (b.price.displayPrice ?? 0))
}

/**
 * Build a `NormaliseContext` from a raw API response object for a specific leg.
 *
 * Centralises the repetitive pattern of extracting metaData / subTravelOptions /
 * fares / nearbyAirportCodes from the wire response. Both `useInitialRtSelection`
 * and the per-leg data hooks call this instead of duplicating the extraction.
 *
 * `leg` controls which filterData leg to read nearby airports from.
 */
export const toNormaliseContext = (
  response: WireSearchResponse,
  leg: 'J1' | 'J2',
  showPerAdult = false,
  opts: {
    isIntl?: boolean
    /** Searched origin IATA (uppercase). When provided, triggers intl nearby logic. */
    searchedOrigin?: string
    /** Searched destination IATA (uppercase). */
    searchedDestination?: string
  } = {}
): NormaliseContext => {
  const filterData = response.filterData ?? {}
  const legFilterData = leg === 'J1' ? filterData.onwardLeg : filterData.returnLeg
  const { isIntl, searchedOrigin, searchedDestination } = opts

  let nearbyAirportCodes: Set<string> | undefined
  if (!searchedOrigin) {
    const nearbyFilter = legFilterData?.nearbyAirportFilter ?? []
    const codes = nearbyFilter.flatMap(n => {
      const primary = (n.airportCode ?? '').toUpperCase()
      return (Array.isArray(n.selectedValues) ? n.selectedValues : []).filter(v => v && v.toUpperCase() !== primary)
    })
    nearbyAirportCodes = codes.length > 0 ? new Set(codes) : undefined
  }

  return {
    metaData: response.metaData ?? {},
    subTravelOptions: response.subTravelOptions ?? {},
    fares: response.fares ?? {},
    flights: response.flights ?? {},
    showPerAdult,
    isIntl,
    nearbyAirportCodes,
    searchedOrigin,
    searchedDestination,
  }
}
